/**
 * Browser back (a phone's back swipe, the back button) closes the topmost open
 * dialog instead of leaving the page under it — for EVERY dialog, from one
 * place, with nothing to wire per dialog.
 *
 * It watches the DOM for open dialogs — our `Modal` (`.Mfw-Modal-overlay`) and
 * everything built on MUI's Modal (`Dialog`, temporary `Drawer`, `Menu`,
 * `Popover`, `Select`'s list) — and keeps one history entry per open dialog,
 * each at the SAME url with the current state copied over (React Router keeps
 * its own index and sees no navigation). Back pops an entry, and the topmost
 * dialog is closed the way it closes on Escape: MUI's through an Escape
 * press, our `Modal` through `MODAL_BACK_CLOSE_EVENT` at its overlay alone.
 *
 * - A dialog that ignores Escape (a blocking confirm, a running upload) stays
 *   open and gets its entry back, so back does nothing there rather than
 *   leaving the page.
 * - A dialog with screens of its own (a preview's drill-down) can claim the
 *   back to step one screen back instead: see `useDialogBackStep`.
 * - An overlay of our own that is neither (the phone nav drawer, a floating
 *   menu) joins by carrying the `data-mfw-overlay` marker and closing itself
 *   on back: see `useOverlayBackClose`.
 * - Closing a dialog any other way (its close button) pops its entry, so
 *   history is left as it was found — unless a route change happened from
 *   inside it (Start → player): then the entry stays, at the page's own url,
 *   and back and forward step over it.
 */

import { MODAL_BACK_CLOSE_EVENT } from '@mfw/components';

const DEPTH_KEY = 'mfwDialogDepth';
export const OVERLAY_MARKER = 'data-mfw-overlay';
const OPEN_DIALOG_SELECTOR = `.Mfw-Modal-overlay, .MuiModal-root:not(.MuiModal-hidden), [${OVERLAY_MARKER}]`;
/** MUI's exit transitions run 195–225ms; a closing dialog is still in the DOM. */
const CLOSE_SETTLE_MS = 400;

export const DIALOG_BACK_EVENT = 'mfw:dialog-back';
export type DialogBackEvent = CustomEvent<{ dialog: Element }>;

const readState = () =>
  (window.history.state as null | Record<string, unknown>) ?? {};

const entryDepth = () => {
  const depth = readState()[DEPTH_KEY];
  return typeof depth === 'number' ? depth : 0;
};

/**
 * How many dialog entries the history stands on. A programmatic step back to
 * the previous page from inside a dialog (`useNavigate(-1)`) steps over them
 * too, or it only steps off a dialog's entry and the page stays.
 */
export const dialogEntriesOnTop = entryDepth;

/** React Router's index of the entry; ours copy the one of the page under. */
const entryIndex = () => {
  const idx = readState().idx;
  return typeof idx === 'number' ? idx : undefined;
};

/** Started handlers: with none, nothing would ever release held work. */
let running = 0;
/** Work waiting for the history to step off a closed dialog's entry. */
let afterLeft: Array<() => void> = [];
/** The longest held work waits for a clean-up that may not come. */
const AFTER_LEFT_MAX_MS = 1000;

const releaseAfterLeft = () => {
  const work = afterLeft;
  afterLeft = [];
  work.forEach((run) => run());
};

/**
 * Runs `work` once the history has stepped off the entry of the dialog that
 * is closing — for a change that has to land on the PAGE's entry, such as a
 * filter's Apply writing the url with `replace`. Made while the history
 * still stands on the dialog's entry, it replaces that entry instead, and the
 * page is left under it with the old filter, one extra back away. Close the
 * dialog first, then hand the change over.
 */
export function afterDialogEntryLeft(work: () => void) {
  if (running === 0 || entryDepth() === 0) {
    work();
    return;
  }
  afterLeft.push(work);
  setTimeout(() => {
    if (!afterLeft.includes(work)) return;
    afterLeft = afterLeft.filter((held) => held !== work);
    work();
  }, AFTER_LEFT_MAX_MS);
}

const openDialogs = () =>
  Array.from(document.querySelectorAll(OPEN_DIALOG_SELECTOR));

const closeDialog = (dialog: Element) => {
  // Our Modal hears Escape at window — every open one would close — so it
  // gets an event of its own at its own overlay. MUI closes on an Escape
  // keydown at the dialog itself.
  if (dialog.classList.contains('Mfw-Modal-overlay')) {
    dialog.dispatchEvent(new CustomEvent(MODAL_BACK_CLOSE_EVENT));
  } else {
    dialog.dispatchEvent(
      new KeyboardEvent('keydown', {
        bubbles: true,
        cancelable: true,
        key: 'Escape',
      }),
    );
  }
};

/** The slice of React Router's data router this needs. */
export interface NavigationSource {
  state: { navigation: { state: string } };
  subscribe: (
    listener: (state: { navigation: { state: string } }) => void,
  ) => () => void;
}

export function startDialogBrowserBack(router?: NavigationSource) {
  /** How many of our entries the history currently stands on. */
  let depth = entryDepth();
  /** Bumped on every push, so a deferred clean-up can tell it went stale. */
  let generation = 0;
  let unsubscribeRouter: (() => void) | undefined;
  /** The router index of the entry we stand on: a back lands lower. */
  let index = entryIndex();
  /**
   * Router keys of left-over entries a back has crossed from a later page:
   * that page follows them, so forward can step over them onto it.
   */
  const crossedBack = new Set<unknown>();
  /**
   * Open dialogs in the order they opened, the topmost last — not the DOM's:
   * our Modal portals into #root and MUI into body after it, so a Modal
   * opened over a MUI dialog comes first in the DOM. Dialogs that open in
   * the same frame keep their DOM order among themselves.
   */
  let opened: Element[] = [];
  const openDialogsInOrder = () => {
    const open = openDialogs();
    opened = [
      ...opened.filter((dialog) => open.includes(dialog)),
      ...open.filter((dialog) => !opened.includes(dialog)),
    ];
    return opened;
  };

  // A click that closes a dialog often also navigates (a nav item, Start →
  // player), and lazy routes push their url only once loaded. Popping our
  // entry before then would cancel that navigation, so wait for the router.
  const whenRouterIdle = (run: () => void) => {
    if (!router || router.state.navigation.state === 'idle') {
      run();
      return;
    }
    unsubscribeRouter?.();
    unsubscribeRouter = router.subscribe((state) => {
      if (state.navigation.state !== 'idle') return;
      unsubscribeRouter?.();
      unsubscribeRouter = undefined;
      run();
    });
  };
  /** While dialogs close after a back, their count lags behind `depth`. */
  let settlingUntil = 0;
  let settleTimer: ReturnType<typeof setTimeout> | undefined;

  const sync = () => {
    const count = openDialogsInOrder().length;
    // A route change from inside a dialog: we stand on the new page's entry
    // and the dialogs' entries are behind it. Its own dialog can open in the
    // same frame (Start from a loaded preview), so the count may not move. A
    // `replace` also drops our key, but keeps the router's index: same page.
    const entry = entryIndex();
    if (depth > 0 && entryDepth() === 0 && entry !== index) {
      depth = 0;
      releaseAfterLeft();
    }
    index = entry;
    if (count > depth) {
      if (Date.now() < settlingUntil) return;
      generation += 1;
      // the push drops whatever lay ahead: nothing follows this page's
      // entries any more
      crossedBack.delete(readState().key);
      for (; depth < count; depth += 1) {
        window.history.pushState(
          { ...readState(), [DEPTH_KEY]: depth + 1 },
          '',
        );
      }
    } else if (count < depth) {
      // closed from the dialog itself: pop its entries — but only while we
      // still stand on them, not after a route change from inside it
      const extra = depth - count;
      const staleDepth = depth;
      const staleGeneration = generation;
      depth = count;
      whenRouterIdle(() => {
        index = entryIndex(); // a route left for from inside it is in by now
        const onOurEntry = entryDepth() === staleDepth;
        if (onOurEntry && generation === staleGeneration) {
          window.history.go(-extra); // held work runs on its landing
        } else {
          releaseAfterLeft();
        }
      });
    }
  };

  const handlePopState = () => {
    const target = entryDepth();
    const from = index;
    index = entryIndex();
    if (target > depth) {
      // onto an entry whose dialog is long gone: step off it the way the
      // user went — forward from its own page onto a page known to follow
      // it, else back (a jump from further back meant the page itself)
      const { key } = readState();
      const wentBack =
        from !== undefined && index !== undefined && from > index;
      if (wentBack) crossedBack.add(key);
      const overToPage = from === index && crossedBack.has(key);
      window.history.go(overToPage ? 1 : depth - target);
      return;
    }
    if (target === depth) {
      // our own clean-up landing: off the closed dialog's entry now
      releaseAfterLeft();
      return;
    }

    const dialogs = openDialogsInOrder();
    const closing = Math.min(depth - target, dialogs.length);
    depth = target;
    dialogs
      .slice(dialogs.length - closing)
      .reverse()
      .forEach((dialog) => {
        const event: DialogBackEvent = new CustomEvent(DIALOG_BACK_EVENT, {
          cancelable: true,
          detail: { dialog },
        });
        if (window.dispatchEvent(event)) closeDialog(dialog);
      });

    // a dialog that stepped a screen back or ignored Escape is still open:
    // give it its entry back once the others have had time to close
    settlingUntil = Date.now() + CLOSE_SETTLE_MS;
    clearTimeout(settleTimer);
    settleTimer = setTimeout(() => {
      settlingUntil = 0;
      sync();
    }, CLOSE_SETTLE_MS);
  };

  // the observer sees every class change on the page: count dialogs at most
  // once a frame
  let syncFrame: null | number = null;
  const scheduleSync = () => {
    if (syncFrame !== null) return;
    syncFrame = requestAnimationFrame(() => {
      syncFrame = null;
      sync();
    });
  };

  const observer = new MutationObserver(scheduleSync);
  observer.observe(document.body, {
    attributeFilter: ['class'],
    attributes: true,
    childList: true,
    subtree: true,
  });
  window.addEventListener('popstate', handlePopState);
  running += 1;

  // A reload while a dialog was open lands on its entry with no dialog left.
  if (depth > 0 && openDialogs().length === 0) {
    const stale = depth;
    depth = 0;
    window.history.go(-stale);
  } else {
    sync();
  }

  return () => {
    observer.disconnect();
    unsubscribeRouter?.();
    window.removeEventListener('popstate', handlePopState);
    clearTimeout(settleTimer);
    if (syncFrame !== null) cancelAnimationFrame(syncFrame);
    running -= 1;
    if (running === 0) releaseAfterLeft(); // nothing left to release it
  };
}
